import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import EventNotification from '../interfaces/eventNotification/EventNotification';
import { getNotifications } from '../services/notificationServices/NotificationService';
import { useAuth } from '../firebase/AuthProvider';

interface NotificationState {
    notifications: EventNotification[];
    setNotifications: React.Dispatch<React.SetStateAction<EventNotification[]>>;
    hasUnread: boolean;
    addNewNotification: (notification: EventNotification) => void;
    refreshNotification: () => void;
    markAsAllReadNotification: () => void;
    markNotificationAsReadById: (notificationId : string) => void;
}

const NotificationContext = createContext<NotificationState | undefined>(undefined);

export const NotificationStateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { currentUser } = useAuth();
    const [notifications, setNotifications] = useState<EventNotification[]>([]);
    const hasUnread = notifications.some(n => !n.read);

    useEffect(() => {
        setNotifications([]);
        if (currentUser) {
            currentUser.getIdToken().then(token => {
                getNotificationsForUser(token);
            })
        }
    }, [currentUser]);

    const getNotificationsForUser = async (token: string) => {
        const response = await getNotifications(token, 1, 50);
        if (response !== undefined && response !== null) {
            setNotifications(response);
        }
    };

    const refreshNotification = () => {
        if (currentUser) {
            currentUser.getIdToken().then(token => {
                getNotificationsForUser(token);
            })
        }
    }

    const addNewNotification = (notification: EventNotification) => {
        console.log("Log: Adding new notification to state in NotificationStateContext.tsx", { notification });
        setNotifications((prev) => {
            // If a notification with the same ID already exists, do not add it again.
            if (prev.some(n => n.id === notification.id)) {
                return prev;
            }
            // Otherwise, prepend the new notification.
            return [notification, ...prev];
        });
    };

    const markAsAllReadNotification = () =>{
         setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    }

    const markNotificationAsReadById = (notificationId: string) => {
        setNotifications(prev =>
            prev.map(n =>
                n.id === notificationId ? { ...n, read: true } : n
            )
        );
    };



    return (
        <NotificationContext.Provider value={{ notifications, setNotifications, hasUnread, addNewNotification, refreshNotification, markAsAllReadNotification, markNotificationAsReadById }}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotificationState = (): NotificationState => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotificationState must be used within a NotificationProvider');
    }
    return context;
};
